import React, { useEffect, useState } from "react"
import { withRouter, Link } from "react-router-dom"
import MetaTags from "react-meta-tags"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { Alert, Button, Card, CardBody, Col, Container, Row, Spinner, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { numberWithCommas } from "helpers/helpers"
import {
  mapCampaignApiResponse,
  mapCampaignStatsResponse,
  mapTrendResponse,
  mapRecentAwardsResponse,
  STATUS_META,
  AUDIT_STATUS_META,
} from "./data"
import { getBonusCampaignDetailsCall, pauseBonusCampaignCall } from "helpers/repository"
import { formatDate, formatDateRange } from "./utils"
import BonusTrendChart from "./BonusTrendChart"
import ConfirmDialog from "./ConfirmDialog"
import StatusBadge from "./StatusBadge"
import { useCanManageAffiliateBonuses } from "./permissions"
import { sitename } from "../../../constants/constants"

const DetailRow = ({ label, children }) => (
  <Row className="mb-3">
    <Col sm="5" className="text-muted">
      {label}
    </Col>
    <Col sm="7">{children}</Col>
  </Row>
)

const StatTile = ({ label, value, delta, deltaClass }) => (
  <Col md={3} sm={6} className="mb-3">
    <Card className="mb-0 h-100 ab-card">
      <CardBody>
        <div className="text-muted text-uppercase font-size-11 fw-semibold">{label}</div>
        <div className="font-size-20 fw-bold mt-2">{value}</div>
        <div className={`font-size-12 mt-1 ${deltaClass}`}>{delta}</div>
      </CardBody>
    </Card>
  </Col>
)

const View = props => {
  const urlParams = new URLSearchParams(props.location.search)
  const id = urlParams.get("id")
  const canEdit = useCanManageAffiliateBonuses()
  const [confirm, setConfirm] = useState(null)
  const [pausing, setPausing] = useState(false)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [campaign, setCampaign] = useState(null)
  const [stats, setStats] = useState([])
  const [trend, setTrend] = useState({ days: [], total: 0 })
  const [recentAwards, setRecentAwards] = useState([])

  const loadDetails = () => {
    if (!id) return
    setLoading(true)
    setLoadError(false)
    getBonusCampaignDetailsCall(id)
      .then(res => {
        const payload = res?.data?.data ?? {}
        const mappedCampaign = mapCampaignApiResponse(payload.campaign)
        setCampaign(mappedCampaign)
        setStats(mapCampaignStatsResponse(payload.stats, mappedCampaign))
        setTrend(mapTrendResponse(payload.trend, payload.trend_total))
        setRecentAwards(mapRecentAwardsResponse(payload.recent_awards).slice(0, 5))
      })
      .catch(err => {
        console.error(err)
        setLoadError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(loadDetails, [id])

  const closeConfirm = () => setConfirm(null)
  const handleDisable = () => {
    setConfirm({
      title: "Disable this campaign?",
      body: "New registrations will stop receiving this bonus immediately. You can re-enable it later.",
      confirmLabel: "Disable",
      onConfirm: () => {
        setPausing(true)
        pauseBonusCampaignCall(id)
          .then(res => {
            toastr.success(res?.data?.message || "Campaign paused.")
            closeConfirm()
            loadDetails()
          })
          .catch(err => {
            console.error(err)
            toastr.error(err?.response?.data?.message || "Could not pause the campaign. Please try again.")
            closeConfirm()
          })
          .finally(() => setPausing(false))
      },
    })
  }

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Affiliate" breadcrumbItem="Campaign" />
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    )
  }

  if (loadError || !campaign) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Affiliate" breadcrumbItem="Campaign" />
          <Card>
            <CardBody>
              {loadError ? "Could not load this campaign. Please try again." : "Campaign not found."}
            </CardBody>
          </Card>
        </Container>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{sitename}</title>
        </MetaTags>
        <style>{`
          .ab-card { border-radius: 10px; overflow: hidden; }
          .btn.ab-header-btn { border-radius: 8px; }
          .ab-back-btn { width: 38px; height: 38px; border-radius: 8px; padding: 0; }
        `}</style>
        <Container fluid>
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-light ab-back-btn d-inline-flex align-items-center justify-content-center"
                onClick={() => props.history.push("/affiliate/bonus-configuration")}
              >
                <i className="mdi mdi-arrow-left font-size-18" />
              </button>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h4 className="mb-0 font-size-18">{campaign.name}</h4>
                  <StatusBadge status={campaign.status} meta={STATUS_META} />
                </div>
                <p className="text-muted mb-0 font-size-12">
                  {campaign.subtitle} · Created {formatDate(campaign.createdDate)}
                </p>
              </div>
            </div>
            {canEdit && (
              <div className="d-flex gap-2">
                <Button
                  color="outline-primary"
                  className="ab-header-btn"
                  onClick={() =>
                    props.history.push({
                      pathname: `/affiliate/bonus-configuration/edit`,
                      search: `?id=${campaign.id}`,
                      state: { campaign },
                    })
                  }
                >
                  <i className="mdi mdi-pencil me-1" />
                  Edit
                </Button>
                {campaign.status === "Active" && (
                  <Button color="outline-danger" className="ab-header-btn" disabled={pausing} onClick={handleDisable}>
                    {pausing && <Spinner size="sm" className="me-1" />}
                    <i className="mdi mdi-cancel me-1" />
                    Disable
                  </Button>
                )}
              </div>
            )}
          </div>

          {campaign.status === "Expired" && (
            <Alert color="warning">
              <b>Campaign expired</b> on {formatDate(campaign.endDate)} — it is no longer awarding bonuses.
            </Alert>
          )}
          {campaign.status === "Paused" && (
            <Alert color="warning">
              <b>Campaign paused.</b> New registrations will not receive this bonus until it is resumed.
            </Alert>
          )}
          {campaign.status === "Archived" && (
            <Alert color="secondary">
              <b>Campaign archived.</b> It is retained for reporting only and no longer awards bonuses.
            </Alert>
          )}

          <Row>
            {stats.map(st => (
              <StatTile key={st.label} {...st} />
            ))}
          </Row>

          <Row>
            <Col lg={5}>
              <Card className="ab-card">
                <h5 className="card-header bg-transparent border-bottom">Campaign Summary</h5>
                <CardBody>
                  <DetailRow label="Bonus Type">{campaign.bonusType}</DetailRow>
                  <DetailRow label="Bonus Amount">{campaign.amount}</DetailRow>
                  <DetailRow label="Wagering Requirement">{campaign.wageringRequirement}</DetailRow>
                  <DetailRow label="Expiry Period">{campaign.expiryDays} days</DetailRow>
                  <DetailRow label="Country">{campaign.country}</DetailRow>
                  <DetailRow label="Affiliate">{campaign.affiliate}</DetailRow>
                  <DetailRow label="Duration">{formatDateRange(campaign.startDate, campaign.endDate)}</DetailRow>
                  <DetailRow label="Max Claims">{numberWithCommas(campaign.maxClaims)}</DetailRow>
                  <DetailRow label="Last Updated">{formatDate(campaign.lastUpdated)}</DetailRow>
                </CardBody>
              </Card>
            </Col>
            <Col lg={7}>
              <Card className="ab-card">
                <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Bonuses Awarded — last 7 days</h5>
                  <span className="text-muted font-size-12">{trend.total} this week</span>
                </div>
                <CardBody>
                  <BonusTrendChart trend={trend} />
                </CardBody>
              </Card>
              <Card className="ab-card">
                <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Recent Awards</h5>
                  <Link to="/affiliate/bonus-configuration" className="font-size-12 fw-semibold">
                    View all →
                  </Link>
                </div>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Player</th>
                          <th className="text-end">Amount</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentAwards.length === 0 && (
                          <tr>
                            <td colSpan={4} className="text-center text-muted py-3">
                              No award events yet for this campaign.
                            </td>
                          </tr>
                        )}
                        {recentAwards.map(a => (
                          <tr key={a.id}>
                            <td className="fw-bold">{a.player}</td>
                            <td className="text-end">{a.amount}</td>
                            <td className="text-muted">{formatDate(a.awardDate)}</td>
                            <td>
                              <StatusBadge status={a.status} meta={AUDIT_STATUS_META} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={closeConfirm}
      />
    </React.Fragment>
  )
}

export default withRouter(View)
